import * as Ids from '@utils/ids';
import { orderMasterMessageHub as messageHub } from '@services/messageHub/instance';
import {
    NewOrderDetailMessage,
    isPhfOrderVo,
    OrderBusinessType,
} from '@services/messageHub/instance/customSource/newOrderDetailPoller';

import { getGrayStateByToken, GRAY_AB_TOKEN } from '@utils/grayAB';

import { OrderDetailInfo, OrderTagCode } from '@business/pages/OrderManage/wmUnifiedOrder/types';
import { UnprocessedOrderType } from '@business/pages/OrderManage/shared/types';

import { ifPoiEnableAutoAcceptLocally } from '@utils/poiHelper';
import {
    reportOrderFingerprint,
    PC_ORDER_FINGERPRINT_ID,
} from '@utils/report/orderFingerprintReport';
import { recordReceiptInfo } from '@utils/report/orderReceiptReport';
import * as orderReport from '@utils/report/orderArrivalRateReport';
import { reportCustomMetrics, CUSTOM_METRICS } from '@utils/owlReporter';

import { confirmOrderAndPrint, updateOrderCount } from '../../connection/eventHandler/order';
import { addOrderNotification } from '../../connection/eventHandler/pushNotification';
import bellService from '../../service/bell';

/** 餐饮新形态灰度 */
const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);

export default () => {
    messageHub
        .getCustomSource<NewOrderDetailMessage>('newOrderDetailPoller')
        .subscribe(({ newOrders, dataChannel, newIndexOrders }) => {
            // 先用 newIndexOrders 字段，若不为空则处理 newIndexOrders 中的每一个新订单；
            if (newIndexOrders?.length) {
                // 需要被自动接单的部分
                // 为了维持与原有逻辑（需要判断所有订单是否都需要被自动接单）一致，这里的 shouldAutoAcceptOrders 包括拼好饭订单
                const shouldAutoAcceptOrders = newIndexOrders
                    .map(order => ({
                        wmPoiId: order.commonInfo.wmPoiId,
                        wmOrderViewId: order.commonInfo.wmOrderViewId,
                    }))
                    .filter(({ wmPoiId }) => ifPoiEnableAutoAcceptLocally(String(wmPoiId)));

                // 拼好饭订单的详情，由拼好饭分流处理；这里仅需要处理普通订单
                const wmOrders = newIndexOrders.filter(
                    order => order.businessType === OrderBusinessType.WM,
                );

                wmOrders.forEach(order => {
                    reportCustomMetrics(CUSTOM_METRICS.ORDER_FROM_POLLER.KEY, 1, {
                        [CUSTOM_METRICS.ORDER_FROM_POLLER.TAGS.BUSINESS_TYPE]: order.businessType,
                        [CUSTOM_METRICS.ORDER_FROM_POLLER.TAGS.IS_INDEX_VO]: 1,
                        [CUSTOM_METRICS.ORDER_FROM_POLLER.TAGS.INITIATOR]: 'message_hub',
                        [CUSTOM_METRICS.ORDER_FROM_POLLER.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY
                            ? 1
                            : 0,
                    });
                });

                wmOrders
                    .filter(wmOrder =>
                        shouldAutoAcceptOrders.some(
                            order => order.wmOrderViewId === wmOrder.commonInfo.wmOrderViewId,
                        ),
                    )
                    .forEach(({ businessType, commonInfo: { wmPoiId, wmOrderViewId } }) => {
                        confirmOrderAndPrint(
                            {
                                wmPoiId,
                                wmOrderViewId,
                                businessType,
                            },
                            'auto_loop',
                        );
                        // 订单指纹上报
                        reportOrderFingerprint(
                            wmOrderViewId,
                            `${wmPoiId}`,
                            PC_ORDER_FINGERPRINT_ID.NEW_ORDER_POLLING_ARRIVE,
                            0,
                        );
                    });

                if (shouldAutoAcceptOrders.length === newIndexOrders.length) {
                    // 全部订单都是自动接单门店的订单，则播放自动接单语音
                    if (
                        newIndexOrders.some(
                            order =>
                                order.businessType === OrderBusinessType.WM &&
                                order.commonInfo.orderTag === OrderTagCode.YXS,
                        )
                    ) {
                        // 其中有至少1个订单为优享送订单，播放特定铃声
                        bellService.playBell('yxsNewOrder');
                    } else {
                        // 否则播放通用铃声
                        bellService.playBell('autoneworder');
                    }

                    addOrderNotification({
                        content: '美团外卖自动接单，刚刚为您接到新订单了',
                        href: getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER)
                            ? '/new_fe/orderbusiness#/order/unprocessed'
                            : '/new_fe/business#/order/unprocessed',
                    });
                    updateOrderCount({
                        [UnprocessedOrderType.Auto]: '0',
                        noticeInner: true,
                    });
                } else {
                    // 有任何一个订单不是自动接单门店的订单，则更新数量，并播放接单语音
                    bellService.playBell('neworder');
                    addOrderNotification({
                        content: '您有新订单了',
                        href: getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER)
                            ? '/new_fe/orderbusiness#/order/unprocessed?tab=new'
                            : '/new_fe/business#/order/unprocessed?tab=newOrder',
                    });
                }

                recordReceiptInfo(
                    wmOrders.map(({ commonInfo: { wmPoiId, wmOrderViewId } }) => {
                        return {
                            wmPoiId: `${wmPoiId}`,
                            wmOrderViewId,
                        };
                    }),
                    false,
                );

                orderReport.reportEvent(
                    orderReport.ORDER_ARRIVE_EVENT.POLLING_DETAIL_SUCCESS,
                    wmOrders.map(({ commonInfo }) => commonInfo.wmOrderViewId),
                );
            } else if (newOrders.length > 0) {
                // TODO: 待 newIndexOrders 全量后，可以删除这个分支
                reportCustomMetrics(
                    CUSTOM_METRICS.PHFWITHWM_CONFIRM.KEY,
                    1,
                    {},
                    {
                        acctId: Ids.getAcctId(),
                        wmPoiId: Ids.getWmPoiId(),
                    },
                );
                // 需要被自动接单的部分
                // 为了维持与原有逻辑（需要判断所有订单是否都需要被自动接单）一致，这里的 shouldAutoAcceptOrders 包括拼好饭订单
                const shouldAutoAcceptOrders = newOrders
                    .map(order => ({
                        wmPoiId: isPhfOrderVo(order)
                            ? order.wm_poi_id
                            : order.orderInfo.basicVo.wmPoiId,
                        wmOrderViewId: isPhfOrderVo(order)
                            ? order.wm_order_id_view_str
                            : order.orderInfo.wmOrderViewId,
                    }))
                    .filter(({ wmPoiId }) => ifPoiEnableAutoAcceptLocally(String(wmPoiId)));

                // 拼好饭订单的详情，由拼好饭分流处理；这里仅需要处理普通订单
                const wmOrders = newOrders.filter(
                    order => !isPhfOrderVo(order),
                ) as OrderDetailInfo[];

                wmOrders.forEach(order => {
                    reportCustomMetrics(CUSTOM_METRICS.ORDER_FROM_POLLER.KEY, 1, {
                        [CUSTOM_METRICS.ORDER_FROM_POLLER.TAGS.BUSINESS_TYPE]: order.businessType,
                        [CUSTOM_METRICS.ORDER_FROM_POLLER.TAGS.IS_INDEX_VO]: 0,
                        [CUSTOM_METRICS.ORDER_FROM_POLLER.TAGS.INITIATOR]: 'message_hub',
                        [CUSTOM_METRICS.ORDER_FROM_POLLER.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY
                            ? 1
                            : 0,
                    });
                });

                wmOrders
                    .filter(wmOrder =>
                        shouldAutoAcceptOrders.some(
                            order => order.wmOrderViewId === wmOrder.orderInfo.wmOrderViewId,
                        ),
                    )
                    .forEach(order => {
                        const {
                            orderInfo: {
                                wmOrderViewId,
                                basicVo: { wmPoiId },
                            },
                        } = order;

                        confirmOrderAndPrint(
                            {
                                wmPoiId,
                                wmOrderViewId,
                                businessType: order.businessType,
                            },
                            'auto_loop',
                        );
                        // 订单指纹上报
                        reportOrderFingerprint(
                            wmOrderViewId,
                            `${wmPoiId}`,
                            PC_ORDER_FINGERPRINT_ID.NEW_ORDER_POLLING_ARRIVE,
                            0,
                        );
                    });

                if (shouldAutoAcceptOrders.length === newOrders.length) {
                    // 全部订单都是自动接单门店的订单，则播放自动接单语音
                    if (
                        newOrders.some(
                            order =>
                                !isPhfOrderVo(order) &&
                                order.orderInfo.orderTag === OrderTagCode.YXS,
                        )
                    ) {
                        // 其中有至少1个订单为优享送订单，播放特定铃声
                        bellService.playBell('yxsNewOrder');
                    } else {
                        // 否则播放通用铃声
                        bellService.playBell('autoneworder');
                    }

                    addOrderNotification({
                        content: '美团外卖自动接单，刚刚为您接到新订单了',
                        href: getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER)
                            ? '/new_fe/orderbusiness#/order/unprocessed'
                            : '/new_fe/business#/order/unprocessed',
                    });
                    updateOrderCount({
                        [UnprocessedOrderType.Auto]: '0',
                        noticeInner: true,
                    });
                } else {
                    // 有任何一个订单不是自动接单门店的订单，则更新数量，并播放接单语音
                    bellService.playBell('neworder');
                    addOrderNotification({
                        content: '您有新订单了',
                        href: getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER)
                            ? '/new_fe/orderbusiness#/order/unprocessed?tab=new'
                            : '/new_fe/business#/order/unprocessed?tab=newOrder',
                    });
                }

                recordReceiptInfo(
                    wmOrders.map(({ orderInfo }) => {
                        return {
                            wmPoiId: `${orderInfo.basicVo.wmPoiId}`,
                            wmOrderViewId: orderInfo.wmOrderViewId,
                        };
                    }),
                    false,
                );

                orderReport.reportEvent(
                    orderReport.ORDER_ARRIVE_EVENT.POLLING_DETAIL_SUCCESS,
                    wmOrders.map(({ orderInfo }) => orderInfo.wmOrderViewId),
                );
            } else if (
                dataChannel &&
                'newOrderCount' in dataChannel &&
                dataChannel.newOrderCount > 0
            ) {
                bellService.playBell('neworder');
                addOrderNotification({
                    content: '您有新订单了',
                    href: getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER)
                        ? '/new_fe/orderbusiness#/order/unprocessed?tab=new'
                        : '/new_fe/business#/order/unprocessed?tab=newOrder',
                });
            }

            // 更新全部待处理tab的count
            if (dataChannel && 'newOrderCount' in dataChannel) {
                updateOrderCount({
                    [UnprocessedOrderType.NewOrder]: `${dataChannel.newOrderCount || 0}`,
                    [UnprocessedOrderType.PrepDelivery]: `${dataChannel.prepLogisticsCount || 0}`,
                    [UnprocessedOrderType.DeliveryException]: `${dataChannel.logisticsAbnormalCount ||
                        0}`,
                    [UnprocessedOrderType.PrepMeal]: `${dataChannel.prepMealCount || 0}`,
                    [UnprocessedOrderType.Unnormal]: `${dataChannel.mealAbnormalCount || 0}`,
                    [UnprocessedOrderType.Refund]: `${dataChannel.refundCount || 0}`,
                    [UnprocessedOrderType.FoodSafeProcess4Poi]: `${dataChannel.foodSafeProcess4PoiCount ||
                        0}`,
                    [UnprocessedOrderType.Remind]: `${dataChannel.reminderCount || 0}`,
                    [UnprocessedOrderType.LossCompensate]: `${dataChannel.cansunCount || 0}`,
                    [UnprocessedOrderType.DutyCompensate]: `${dataChannel.responsibleCount || 0}`,
                    [UnprocessedOrderType.Other]: `${dataChannel.etaModifyCount || 0}`,

                    // 新形态增加部分
                    [UnprocessedOrderType.PrepLogistics]: `${dataChannel.prepLogisticsCount || 0}`,
                    [UnprocessedOrderType.LogisticsAbnormal]: `${dataChannel.logisticsAbnormalCount ||
                        0}`,
                    [UnprocessedOrderType.Cancel]: `${dataChannel.cancelCount || 0}`,
                    [UnprocessedOrderType.New]: `${dataChannel.newOrderCount || 0}`,
                    [UnprocessedOrderType.PreOrderToMeal]: `${dataChannel.preOrderToMealCount ||
                        0}`,
                    [UnprocessedOrderType.IncomeAbnormal]: `${dataChannel.incomeAbnormalCount ||
                        0}`,
                    [UnprocessedOrderType.AfterSaleCanAppeal]: `${dataChannel.afterSaleCanAppealCount ||
                        0}`,
                    [UnprocessedOrderType.Responsible]: `${dataChannel.responsibleCount || 0}`,
                    [UnprocessedOrderType.Cansun]: `${dataChannel.cansunCount || 0}`,
                    [UnprocessedOrderType.FoodSafeAppealForPoi]: `${dataChannel.foodSafeAppeal4PoiCount ||
                        0}`,
                    // 待骑手接单
                    [UnprocessedOrderType.OrderPreRiderOrder]: `${dataChannel?.order_preRiderOrder_count ||
                        0}`,
                    // 待骑手到店
                    [UnprocessedOrderType.OrderPreRiderPick]: `${dataChannel?.order_preRiderPick_count ||
                        0}`,
                    // 配送中
                    [UnprocessedOrderType.OrderLogisticsing]: `${dataChannel?.order_logisticsing_count ||
                        0}`,
                    noticeInner: true,
                    forbiddenReload: true,
                });
            }
        });
};
